import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  paymentApi,
  ReconciliationActionContext,
  ReconciliationRecord,
} from '../../api/paymentApi';

const formatMoney = (value: number) =>
  new Intl.NumberFormat('vi-VN').format(Math.round(value));

interface ReconciliationSurplusModalProps {
  isOpen: boolean;
  buildingId: string;
  row: ReconciliationRecord;
  onClose: () => void;
  onSuccess: () => void;
}

export const ReconciliationSurplusModal: React.FC<ReconciliationSurplusModalProps> = ({
  isOpen,
  buildingId,
  row,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [context, setContext] = useState<ReconciliationActionContext | null>(null);
  const [revenueCategoryId, setRevenueCategoryId] = useState('');
  const [projectBankAccountId, setProjectBankAccountId] = useState('');
  const [apartmentId, setApartmentId] = useState('');
  const [apartmentSearch, setApartmentSearch] = useState('');
  const [apartmentOptions, setApartmentOptions] = useState<
    Array<{ id: string; code: string; name?: string | null }>
  >([]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    paymentApi
      .getReconciliationActionContext(row.id, buildingId)
      .then((data) => {
        setContext(data);
        setRevenueCategoryId(data.revenueCategories[0]?.id || '');
        setProjectBankAccountId(data.suggestedProjectBankAccountId || '');
        if (data.suggestedApartment) {
          setApartmentId(data.suggestedApartment.id);
          setApartmentSearch(data.suggestedApartment.code);
          setApartmentOptions([data.suggestedApartment]);
        } else {
          const code = row.apartmentCode || row.inferredApartmentCode;
          setApartmentSearch(code || '');
          setApartmentId('');
          setApartmentOptions([]);
        }
      })
      .catch(() => {
        toast.error('Không tải được thông tin giao dịch');
        onClose();
      })
      .finally(() => setLoading(false));
  }, [isOpen, row.id, buildingId, row.apartmentCode, row.inferredApartmentCode, onClose]);

  useEffect(() => {
    if (!isOpen || !buildingId || apartmentSearch.trim().length < 2) return;
    const timer = setTimeout(async () => {
      try {
        const list = await paymentApi.searchApartmentMatches(buildingId, apartmentSearch.trim());
        setApartmentOptions(list);
        if (list.length === 1) {
          setApartmentId(list[0].id);
        }
      } catch {
        setApartmentOptions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [apartmentSearch, buildingId, isOpen]);

  const handleSave = async () => {
    if (!revenueCategoryId || !projectBankAccountId || !apartmentId) {
      toast.error('Vui lòng chọn đủ loại phiếu thu, tài khoản dự án và căn hộ');
      return;
    }
    setSaving(true);
    try {
      await paymentApi.applySurplusDeposit({
        gatewayTransactionId: row.id,
        buildingId,
        apartmentId,
        revenueCategoryId,
        projectBankAccountId,
      });
      toast.success('Đã nạp tiền vào tài khoản căn hộ');
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Không thể nạp tiền');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const amount = context?.transaction.amount ?? row.amount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="border-b px-5 py-4">
          <h3 className="text-lg font-semibold text-gray-900">Nạp tiền vào tài khoản</h3>
          <p className="text-sm text-gray-500 mt-1 truncate" title={row.description}>
            {row.description || '—'}
          </p>
        </div>

        {loading ? (
          <div className="px-5 py-10 text-center text-gray-500">Đang tải...</div>
        ) : (
          <div className="px-5 py-4 space-y-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Loại phiếu thu <span className="text-red-500">*</span>
              </label>
              <select
                value={revenueCategoryId}
                onChange={(e) => setRevenueCategoryId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Loại phiếu thu</option>
                {context?.revenueCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Tài khoản dự án <span className="text-red-500">*</span>
              </label>
              <select
                value={projectBankAccountId}
                onChange={(e) => setProjectBankAccountId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Tài khoản dự án</option>
                {context?.projectBankAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                    {account.bankAccountNumber ? ` (${account.bankAccountNumber})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Căn hộ <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={apartmentSearch}
                onChange={(e) => {
                  setApartmentSearch(e.target.value);
                  setApartmentId('');
                }}
                placeholder="Mã căn hộ"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-2"
              />
              {apartmentOptions.length > 0 && (
                <select
                  value={apartmentId}
                  onChange={(e) => {
                    setApartmentId(e.target.value);
                    const selected = apartmentOptions.find((a) => a.id === e.target.value);
                    if (selected) setApartmentSearch(selected.code);
                  }}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Chọn căn hộ</option>
                  {apartmentOptions.map((apt) => (
                    <option key={apt.id} value={apt.id}>
                      {apt.code}{apt.name ? ` — ${apt.name}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <p className="text-sm text-gray-600 bg-gray-50 rounded-md px-3 py-2">
              Bạn đang thực hiện việc nạp số tiền{' '}
              <strong>{formatMoney(amount)}đ</strong> vào tài khoản căn hộ (phiếu thu dư trong Hoạt động thu).
            </p>
          </div>
        )}

        <div className="border-t px-5 py-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Đóng
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || saving}
            className="px-4 py-2 text-sm bg-cyan-600 text-white rounded-md hover:bg-cyan-700 disabled:opacity-60"
          >
            {saving ? 'Đang lưu...' : 'Lưu'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReconciliationSurplusModal;
